import * as React from 'react'

import { BlockChangeStatus } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { match } from 'ts-pattern'
import { ContributorsTooltip } from '../contributors-tooltip'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

export const StatusIcon = ({
  status,
  isConflictResolved,
  className,
  disableTooltip,
  blameProps,
}: {
  status: BlockChangeStatus
  isConflictResolved?: boolean
  className?: string
  disableTooltip?: boolean
  blameProps?: {
    sourceBlockId: string
    rootId: string | null
  }
}) => {
  const commonClassName = clsx(className, 'rounded-full')
  const replicache = useRepoReplicache().data?.replicache

  const { data: blockMilestones } = useCustomSyncerSub(replicache, {
    syncer: 'multiBlockMilestones',
    params: {
      rootId: blameProps?.rootId || '',
      sourceBlockIds: blameProps?.sourceBlockId
        ? [blameProps.sourceBlockId]
        : [],
    },
  })
  const blockMilestone = blockMilestones?.[0] ?? null

  const children = (
    <span>
      {match(status)
        .with('created', () => (
          <PlusCircledIcon className={clsx(commonClassName, 'text-green')} />
        ))
        .with('deleted', () => (
          <MinusCircledIcon
            className={clsx(commonClassName, 'text-dark-error')}
          />
        ))
        .with('reordered', () => (
          <ModifiedIcon className={clsx(commonClassName, 'text-dark-muted')} />
        ))
        .with('modified', () => (
          <ModifiedIcon className={clsx(commonClassName, 'text-blue')} />
        ))
        .with('conflict', () => {
          return isConflictResolved ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-green"
            >
              <rect
                x="0.794922"
                y="0.795288"
                width="12.4093"
                height="12.4093"
                rx="6.20464"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.794922 6.99993C0.794922 3.57321 3.57284 0.795288 6.99957 0.795288C10.4263 0.795288 13.2042 3.5732 13.2042 6.99993C13.2042 10.4266 10.4263 13.2046 6.99957 13.2046C3.57284 13.2046 0.794922 10.4266 0.794922 6.99993ZM9.36824 4.5184C9.57901 4.66672 9.62964 4.95783 9.48132 5.1686L6.52576 9.3686C6.44606 9.48187 6.32016 9.55388 6.18212 9.56516C6.04407 9.57643 5.90816 9.52581 5.81112 9.42698L4.33335 7.92186C4.15278 7.73795 4.15549 7.44248 4.3394 7.26192C4.52331 7.08135 4.81877 7.08406 4.99934 7.26797L6.08477 8.37349L8.71803 4.63148C8.86636 4.4207 9.15746 4.37007 9.36824 4.5184Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-yellow"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.0669 7.00002C13.0669 10.3505 10.3508 13.0667 7.00026 13.0667C3.64973 13.0667 0.933594 10.3505 0.933594 7.00002C0.933594 3.64949 3.64973 0.93335 7.00026 0.93335C10.3508 0.93335 13.0669 3.64949 13.0669 7.00002ZM6.30026 3.96668C6.30026 3.58008 6.61366 3.26668 7.00026 3.26668C7.38686 3.26668 7.70026 3.58008 7.70026 3.96668V8.16668C7.70026 8.55328 7.38686 8.86668 7.00026 8.86668C6.61366 8.86668 6.30026 8.55328 6.30026 8.16668V3.96668ZM7.00026 9.33335C6.61366 9.33335 6.30026 9.64675 6.30026 10.0333C6.30026 10.4199 6.61366 10.7333 7.00026 10.7333C7.38686 10.7333 7.70026 10.4199 7.70026 10.0333C7.70026 9.64675 7.38686 9.33335 7.00026 9.33335Z"
                fill="currentColor"
              />
            </svg>
          )
        })
        .with('unmodified', () => (
          <MinusCircledIcon
            className={clsx(commonClassName, 'text-dark-faint rotate-90')}
          />
        ))
        .otherwise((s) => (
          <span className={commonClassName}>{s}</span>
        ))}
    </span>
  )

  if (disableTooltip) return children

  return (
    <ContributorsTooltip
      blockMilestone={blockMilestone ?? null}
      content={status}
      side="top"
    >
      {children}
    </ContributorsTooltip>
  )
}

// icons

export const PlusCircledIcon = React.forwardRef<
  SVGSVGElement,
  { color?: string } & React.SVGProps<SVGElement>
>((props, ref) => {
  return (
    <svg
      {...props}
      ref={ref}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 13.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Zm-.75-3.2a.75.75 0 1 0 1.5 0V7.8h2.5a.75.75 0 0 0 0-1.5h-2.5V3.8a.75.75 0 0 0-1.5 0v2.5h-2.5a.75.75 0 0 0 0 1.5h2.5v2.5Z"
        fill="currentColor"
      />
    </svg>
  )
})

export const MinusCircledIcon = React.forwardRef<
  SVGSVGElement,
  { color?: string } & React.SVGProps<SVGElement>
>((props, ref) => {
  return (
    <svg
      {...props}
      ref={ref}
      viewBox="0 0 15 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 14.965a6.964 6.964 0 1 0 0-13.929 6.964 6.964 0 0 0 0 13.929ZM4.018 7.25a.804.804 0 1 0 0 1.608h6.964a.804.804 0 1 0 0-1.608H4.018Z"
        fill="currentColor"
      />
    </svg>
  )
})

const ModifiedIcon = React.forwardRef<
  SVGSVGElement,
  { color?: string } & React.SVGProps<SVGElement>
>((props, ref) => {
  return (
    <svg
      {...props}
      ref={ref}
      viewBox="0 0 15 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 1.036a6.964 6.964 0 1 0 0 13.929 6.964 6.964 0 0 0 0-13.929Zm.107 9.107a2.143 2.143 0 1 0 0-4.285 2.143 2.143 0 0 0 0 4.285Z"
        fill="currentColor"
      />
    </svg>
  )
})
